import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

import Banner3 from '../../assets/img/website-images/Blogdetailsbanner3.png';

/* ─── Variants ─────────────────────────────────────────────────── */
const heroVariants = {
    hidden: { opacity: 0, scale: 1.04 },
    visible: {
        opacity: 1, scale: 1,
        transition: { duration: 1.0, ease: [0.22, 1, 0.36, 1] },
    },
};

const titleVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.15 },
    },
};

/* ─── Paragraph with individual scroll trigger ──────────────────── */
function AnimPara({ children }) {
    const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
    return (
        <motion.p
            ref={ref}
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, ease: 'easeOut' }}
        >
            {children}
        </motion.p>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function BlogThree() {
    const { ref: heroRef,  inView: heroInView  } = useInView({ threshold: 0.2, triggerOnce: true });
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: hero image clip-path wipe */
    const imgWrapRef = useRef(null);
    useEffect(() => {
        if (heroInView && imgWrapRef.current) {
            gsap.fromTo(
                imgWrapRef.current,
                { clipPath: 'inset(0 100% 0 0)', opacity: 1 },
                { clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.inOut', delay: 0.1 }
            );
        }
    }, [heroInView]);

    /* GSAP: hero image scroll parallax */
    const heroImgRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!heroImgRef.current) return;
            const rect     = heroImgRef.current.getBoundingClientRect();
            const progress = 1 - (rect.bottom / (window.innerHeight + rect.height));
            gsap.set(heroImgRef.current, { y: progress * 30 });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    /* GSAP: reading progress bar */
    const progressRef = useRef(null);
    useEffect(() => {
        const handleScroll = () => {
            if (!progressRef.current) return;
            const scrollTop = window.scrollY;
            const docHeight = document.body.scrollHeight - window.innerHeight;
            const progress  = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
            gsap.set(progressRef.current, { width: `${progress}%` });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            {/* Reading progress bar */}
            <div style={{
                position: 'fixed', top: 0, left: 0, height: '3px',
                background: 'transparent', zIndex: 9999, width: '100%',
                pointerEvents: 'none',
            }}>
                <div
                    ref={progressRef}
                    style={{
                        height: '100%', width: '0%',
                        background: 'linear-gradient(90deg, #1E5AA8, #F27024)',
                        borderRadius: '0 2px 2px 0',
                    }}
                />
            </div>

            <section className="project-detail">
                <div className="container">

                    {/* ── Hero Image ── */}
                    <div className="upper-box" ref={heroRef}>
                        <motion.div
                            variants={heroVariants}
                            initial="hidden"
                            animate={heroInView ? 'visible' : 'hidden'}
                            style={{ borderRadius: '12px', overflow: 'hidden' }}
                        >
                            <div ref={imgWrapRef} style={{ overflow: 'hidden', borderRadius: '12px' }}>
                                <figure className="image" style={{ margin: 0, overflow: 'hidden' }}>
                                    <img
                                        ref={heroImgRef}
                                        src={Banner3}
                                        alt="Blog Banner"
                                        style={{ display: 'block', width: '100%', transformOrigin: 'center' }}
                                    />
                                </figure>
                            </div>
                        </motion.div>
                    </div>

                    {/* ── Article Content ── */}
                    <div className="lower-content2">
                        <div className="row">
                            <div className="text-column col-lg-12 col-md-12 col-sm-12">
                                <div className="s-about-content">

                                    {/* Title */}
                                    <motion.h2
                                        ref={titleRef}
                                        variants={titleVariants}
                                        initial="hidden"
                                        animate={titleInView ? 'visible' : 'hidden'}
                                    >
                                        Understanding the Role of a Pharmacist in Today's Healthcare System
                                    </motion.h2>

                                    <AnimPara>
                                        Ask most people what a pharmacist does, and the answer will usually involve
                                        dispensing medicines. While that is certainly part of the role, it barely
                                        scratches the surface of what modern pharmacists contribute to healthcare.
                                        Today, pharmacists are essential members of the clinical team — advisors,
                                        researchers, patient advocates, and innovators who sit at the very heart of
                                        how medicines reach and help people.
                                    </AnimPara>

                                    <AnimPara>
                                        In hospital settings, <strong>Clinical Pharmacists</strong> work hand-in-hand with doctors
                                        and nurses to review medication plans, identify potential drug interactions,
                                        and advise on the most effective and safe treatments for individual patients.
                                        Their knowledge of pharmacokinetics — how drugs behave in the body — and
                                        pharmacodynamics — how drugs affect the body — is invaluable in ensuring
                                        patient safety, especially for complex cases involving multiple medications.
                                    </AnimPara>

                                    <AnimPara>
                                        In the community, pharmacists are often the <strong>most accessible healthcare
                                        professionals</strong> patients can turn to. They counsel patients on how to take
                                        medicines correctly, explain potential side effects, and guide people on
                                        over-the-counter treatments. This advisory role is growing in importance
                                        as healthcare systems face pressure to reduce the burden on doctors and
                                        hospitals by empowering patients to manage their own health more effectively.
                                    </AnimPara>

                                    <AnimPara>
                                        In research and industry, pharmacists contribute to the development of new
                                        drugs, ensuring formulations are safe, stable, and effective before they
                                        ever reach a patient. <strong>Regulatory Affairs professionals</strong> with pharmacy
                                        backgrounds navigate the complex process of getting new medicines approved
                                        by health authorities, while <strong>Pharmacovigilance specialists</strong> monitor drugs
                                        already on the market for any unexpected safety concerns.
                                    </AnimPara>

                                    <AnimPara>
                                        At Akash Institute of Pharmacy, we train our students to see themselves as
                                        more than medicine dispensers. Through our comprehensive B.Pharm curriculum,
                                        we instil the clinical knowledge, ethical values, communication skills, and
                                        professional mindset needed to take on any of these vital roles. The future
                                        of pharmacy is broad, impactful, and full of opportunity — and it begins here.
                                    </AnimPara>

                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>
        </>
    );
}

export default BlogThree;
